import { AppId, FCX, Tooltip, usePlatformContext, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import { HomeRankingTabEnum } from "@enums";
import HomeSectionHeader from "@pages/Home/components/HomeSectionHeader";
import HomeSectionTitle from "@pages/Home/components/HomeSectionTitle";
import HomeSectionDelimiter from "@pages/Home/components/HomeSectionDelimiter";
import HomeSectionLoader from "@pages/Home/components/HomeSectionLoader";
import HomeSectionTabs from "@pages/Home/components/HomeSectionTabs";
import { HOME_TOP_TABS } from "@pages/Home/constants";
import HomeTrendingTable from "./HomeTrendingTable";
import useLocalStorageState from "@hooks/useLocalStorageState";
import SelectFlavorCategories from "@pages/Home/sections/HomeTrending/SelectFlavorCategories";
import { useCallback, useMemo } from "react";
import { useHomeWhatsHotData } from "@apiHooks/useHomeWhatsHotData";
import HomeSectionFooter from "@pages/Home/components/HomeSectionFooter";
import AccessLink from "@components/AccessLink/AccessLink";
import HomeSectionSubTitle from "@pages/Home/components/HomeSectionSubTitle";
import HomeTopFoodBeveragePosts from "@pages/Home/sections/HomeTopFoodBeveragePosts/HomeTopFoodBeveragePosts";
import { AiOutlineInfoCircle } from "react-icons/ai";
import "./HomeTrending.styles.scss";

interface HomeTrendingProps {
    showShortTable?: boolean;
    showTopPosts?: boolean;
}

const HomeTrending: FCX<HomeTrendingProps> = ({ showShortTable, showTopPosts }) => {
    const { isLoading, data: whatsHotData } = useHomeWhatsHotData();
    const { userInfo: { products } } = usePlatformContext();
    const [selectedSubCategoriesIdsRaw, setSelectedSubCategoriesIdsRaw] = useLocalStorageState<string>(
        'home-whats-hot-subcategories',
        '',
    );
    const [activeTab, setActiveTab] = useLocalStorageState<HomeRankingTabEnum>('home-whats-hot-tab', HomeRankingTabEnum.WHATS_GROWING);
    const isSubCategoriesEnabled = usePlatformFeatureFlag('SNAP Whats Hot Page');

    const selectedSubCategoriesIds = useMemo(
        () => {
            if (!isSubCategoriesEnabled || selectedSubCategoriesIdsRaw.trim() === '') return [];
            return selectedSubCategoriesIdsRaw.split(',').map(id => Number(id));
        },
        [isSubCategoriesEnabled, selectedSubCategoriesIdsRaw]
    );

    const setSelectedSubCategoriesIds = useCallback(
        (ids: number[]) => {
            setSelectedSubCategoriesIdsRaw(ids.join(','));
        },
        [setSelectedSubCategoriesIdsRaw]
    );

    const dataRaw = useMemo(
        () => whatsHotData[activeTab] ?? [],
        [whatsHotData, activeTab]
    );

    const filteredData = useMemo(
        () => {
            if (selectedSubCategoriesIds.length === 0) {
                return dataRaw;
            }
            return dataRaw.filter(item =>
                item.subCategoriesIds?.some(id => selectedSubCategoriesIds.includes(id))
            );
        },
        [dataRaw, selectedSubCategoriesIds]
    );

    const sortedData = useMemo(
        () => {
            if (!filteredData) return [];

            const getRankingScore = (item: typeof filteredData[number]) => {
                const topScore = item.mentions ?? -Infinity;
                const growingScore = item.twelveMonthEngagementGrowth ?? -Infinity;

                return activeTab === HomeRankingTabEnum.WHATS_TOP
                    ? topScore
                    : growingScore;
            }

            return [...filteredData].sort(
                (a, b) => getRankingScore(b) - getRankingScore(a)
            );
        },
        [filteredData, activeTab]
    );

    const data = useMemo(
        () => sortedData.slice(0, 100),
        [sortedData]
    );

    const trendingNow = useMemo(
        () => products[AppId.TrendingNow],
        [products]
    );

    return (
        <div className="HomeTrending Home__wrapper">
            <HomeSectionHeader>
                <HomeSectionTitle>Trending Now</HomeSectionTitle>
            </HomeSectionHeader>
            {showTopPosts && (
                <>
                    <div className="Home__sub-wrapper">
                        <HomeSectionHeader className="no-pt">
                            <div className="HomeSectionHeader__part is-small">
                                <HomeSectionSubTitle>Top Food & Beverage Posts</HomeSectionSubTitle>
                                <AiOutlineInfoCircle
                                    className="Home__trending-tooltip"
                                    data-tooltip-id="top-food-beverage-posts"
                                />
                                <Tooltip id="top-food-beverage-posts" place="right" style={{ zIndex: 1 }}>
                                    Recent top food and beverage related posts based on
                                    <br/>
                                    a blend of follower count and viral engagement level
                                </Tooltip>
                            </div>
                        </HomeSectionHeader>
                    </div>
                    <HomeTopFoodBeveragePosts />
                </>
            )}
            <div className="Home__sub-wrapper min-height">
                <HomeSectionHeader className="no-pt">
                    <div className="HomeSectionHeader__part is-small">
                        <HomeSectionSubTitle>key trending terms in Social</HomeSectionSubTitle>
                        <AiOutlineInfoCircle
                            className="Home__trending-tooltip"
                            data-tooltip-id="key-trending-terms-in-social"
                        />
                        <Tooltip id="key-trending-terms-in-social" place="top" style={{ zIndex: 10, maxWidth: 320 }}>
                            <div className="HomeTrending__tooltip-content">
                                <div className="HomeTrending__tooltip-title">Data Details</div>
                                <ul>
                                    <li>updated weekly</li>
                                    <li>cleaned and vetted by Datassential trend experts</li>
                                    <li>sources include: TikTok, Instagram, Pinterest, YouTube, X, Reddit</li>
                                    <li>reflects US & Canadian social activity</li>
                                </ul>
                                <div className="HomeTrending__tooltip-title">Mentions</div>
                                count of total mentions of this term
                                <div className="HomeTrending__tooltip-title">Engagement</div>
                                sum of likes, comment, and shares
                            </div>
                        </Tooltip>
                        {isSubCategoriesEnabled && (
                            <SelectFlavorCategories
                                value={selectedSubCategoriesIds}
                                setValue={setSelectedSubCategoriesIds}
                            />
                        )}
                    </div>
                    <HomeSectionTabs
                        data={HOME_TOP_TABS}
                        activeTabId={activeTab}
                        onItemClick={(id) => setActiveTab(id as HomeRankingTabEnum)}
                    />
                </HomeSectionHeader>
                {isLoading ? (
                    <HomeSectionLoader />
                ) : (
                    <HomeTrendingTable
                        data={data}
                        isGrowingActive={activeTab === HomeRankingTabEnum.WHATS_GROWING}
                        visibleRowsCount={showShortTable ? 5 : 10}
                    />
                )}
                <HomeSectionFooter style={{ marginTop: -40 }}>
                    <AccessLink
                        url={trendingNow?.url || ''}
                        text="More Social Trends & MAC"
                        onContactUsClick={() => {}}
                        testId="linkHomeTrending"
                    />
                </HomeSectionFooter>
            </div>
            <HomeSectionDelimiter colors={['#1DFEEC', '#4FB0F9', '#FF59B9', '#FF0395']}/>
        </div>
    );
}

export default HomeTrending;